/**
 * Mapbox Region to Strapi SEO Page Mapping
 * 
 * This file maps Mapbox region names (from listing.multiLangAddressParts) 
 * to Strapi SEO page URL slugs and localized Strapi display names.
 * 
 * BUSINESS CASE:
 * Match current locale: Mapbox region language → Strapi region language
 * Example: Mapbox French "canton du Tessin" → Strapi French "Tessin"
 * 
 * DATA STRUCTURE:
 * multiLangAddressParts = {
 *   "english": { region: "Lombardy" },    // Mapbox English
 *   "french": { region: "Lombardie" },    // Mapbox French  
 *   "german": { region: "Lombardei" },    // Mapbox German
 *   "italian": { region: "Lombardia" }    // Mapbox Italian
 * }
 * 
 * LOOKUP FLOW:
 * 1. Get region from: listing.publicData.multiLangAddressParts["italian"].region
 *    Example: region = "Lombardia" (Mapbox Italian)
 * 
 * 2. Get SEO slug (language-agnostic): 
 *    getRegionSeoSlug("Lombardia") → "lombardy"
 * 
 * 3. Get localized Strapi name for display:
 *    getRegionStrapiName("Lombardia", "italian") → "Lombardia"
 *    (Note: function accepts both "italian" and "it")
 * 
 * 4. Build URL: /${locale}/outdoor-destinations/${countrySlug}/${seoSlug}
 *    Result: /it-IT/outdoor-destinations/italy/lombardy
 * 
 * 5. Display in breadcrumb: "Lombardia" (Strapi Italian name)
 */

// Helper to convert full language names to short codes
// multiLangAddressParts uses: "english", "french", "german", "italian"
// strapiNames uses: "en", "fr", "de", "it"
const LANG_NAME_TO_CODE = {
  'english': 'en',
  'french': 'fr',
  'german': 'de',
  'italian': 'it',
  // Also support short codes directly
  'en': 'en',
  'fr': 'fr',
  'de': 'de',
  'it': 'it'
};

// Main mapping structure: English region name as primary key
const REGION_DATA = {
  // ============================================================================
  // SWITZERLAND (12 cantons with SEO pages)
  // ============================================================================
  
  'Ticino': {
    seoSlugs: { en: 'ticino', de: 'tessin', fr: 'tessin', it: 'ticino' },
    mapboxNames: { en: 'Ticino', de: 'Kanton Tessin', fr: 'canton du Tessin', it: 'Canton Ticino' },
    strapiNames: { en: 'Ticino', de: 'Tessin', fr: 'Tessin', it: 'Ticino' },
    hasSeoPage: true
  },
  
  'Appenzell Innerrhoden': {
    seoSlugs: { en: 'appenzell', de: 'appenzell', fr: 'appenzell', it: 'appenzello' },
    mapboxNames: { en: 'Appenzell Innerrhoden', de: 'Kanton Appenzell Innerrhoden', fr: 'canton d\'Appenzell Rhodes-Intérieures', it: 'Appenzello Interno' },
    strapiNames: { en: 'Appenzell', de: 'Appenzell', fr: 'Appenzell', it: 'Appenzello' },
    hasSeoPage: true,
    note: 'Strapi uses simplified "Appenzell", Mapbox uses "Appenzell Innerrhoden"'
  },

  'Appenzell Ausserrhoden': {
    seoSlugs: { en: 'appenzell', de: 'appenzell', fr: 'appenzell', it: 'appenzello' },
    mapboxNames: { en: 'Appenzell Ausserrhoden', de: 'Kanton Appenzell Ausserrhoden', fr: 'canton d\'Appenzell Rhodes-Extérieures', it: 'Appenzello Esterno' },
    strapiNames: { en: 'Appenzell', de: 'Appenzell', fr: 'Appenzell', it: 'Appenzello' },
    hasSeoPage: true,
    note: 'Strapi uses simplified "Appenzell", Mapbox uses "Appenzell Innerrhoden"'
  },
  
  'Bern': {
    seoSlugs: { en: 'bern', de: 'bern', fr: 'berne', it: 'berna' },
    mapboxNames: { en: 'Bern', de: 'Kanton Bern', fr: 'canton de Berne', it: 'Canton Berna' },
    strapiNames: { en: 'Bern', de: 'Bern', fr: 'Berne', it: 'Berna' },
    hasSeoPage: true
  },
  
  'Glarus': {
    seoSlugs: { en: 'glarus', de: 'glarus', fr: 'glaris', it: 'glarona' },
    mapboxNames: { en: 'Glarus', de: 'Kanton Glarus', fr: 'canton de Glaris', it: 'Canton Glarona' },
    strapiNames: { en: 'Glarus', de: 'Glarus', fr: 'Glaris', it: 'Glarona' },
    hasSeoPage: true
  },
  
  'Grisons': {
    seoSlugs: { en: 'graubunden', de: 'graubunden', fr: 'grisons', it: 'grigioni' },
    mapboxNames: { en: 'Grisons', de: 'Kanton Graubünden', fr: 'canton des Grisons', it: 'Grigioni' },
    strapiNames: { en: 'Graubünden', de: 'Graubünden', fr: 'Grisons', it: 'Grigioni' },
    hasSeoPage: true,
    note: 'Mapbox EN uses "Grisons", Strapi EN uses "Graubünden"'
  },
  
  'Jura': {
    seoSlugs: { en: 'jura', de: 'jura', fr: 'jura', it: 'giura' },
    mapboxNames: { en: 'Jura', de: 'Kanton Jura', fr: 'canton du Jura', it: 'Canton Giura' },
    strapiNames: { en: 'Jura', de: 'Jura', fr: 'Jura', it: 'Giura' },
    hasSeoPage: true
  },
  
  'Lucerne': {
    seoSlugs: { en: 'lucerne', de: 'luzern', fr: 'lucerne', it: 'lucerna' },
    mapboxNames: { en: 'Lucerne', de: 'Kanton Luzern', fr: 'canton de Lucerne', it: 'Lucerna' },
    strapiNames: { en: 'Lucerne', de: 'Luzern', fr: 'Lucerne', it: 'Lucerna' },
    hasSeoPage: true
  },
  
  'Nidwalden': {
    seoSlugs: { en: 'nidwalden', de: 'nidwalden', fr: 'nidwald', it: 'nidvaldo' },
    mapboxNames: { en: 'Nidwalden', de: 'Kanton Nidwalden', fr: 'canton de Nidwald', it: 'Nidvaldo' },
    strapiNames: { en: 'Nidwalden', de: 'Nidwalden', fr: 'Nidwald', it: 'Nidvaldo' },
    hasSeoPage: true
  },
  
  'Obwalden': {
    seoSlugs: { en: 'obwalden', de: 'obwalden', fr: 'obwald', it: 'obvaldo' },
    mapboxNames: { en: 'Obwalden', de: 'Kanton Obwalden', fr: 'canton d\'Obwald', it: 'Obvaldo' },
    strapiNames: { en: 'Obwalden', de: 'Obwalden', fr: 'Obwald', it: 'Obvaldo' },
    hasSeoPage: true
  },
  
  'Schwyz': {
    seoSlugs: { en: 'schwyz', de: 'schwyz', fr: 'schwytz', it: 'svitto' },
    mapboxNames: { en: 'Schwyz', de: 'Kanton Schwyz', fr: 'canton de Schwytz', it: 'Canton Svitto' },
    strapiNames: { en: 'Schwyz', de: 'Schwyz', fr: 'Schwytz', it: 'Svitto' },
    hasSeoPage: true
  },
  
  'St. Gallen': {
    seoSlugs: { en: 'st-gallen', de: 'st-gallen', fr: 'saint-gall', it: 'san-gallo' },
    mapboxNames: { en: 'St. Gallen', de: 'Kanton St. Gallen', fr: 'canton de Saint-Gall', it: 'San Gallo' },
    strapiNames: { en: 'St. Gallen', de: 'St. Gallen', fr: 'Saint-Gall', it: 'San Gallo' },
    hasSeoPage: true
  },
  
  'Uri': {
    seoSlugs: { en: 'uri', de: 'uri', fr: 'uri', it: 'uri' },
    mapboxNames: { en: 'Uri', de: 'Kanton Uri', fr: 'canton d\'Uri', it: 'Uri' },
    strapiNames: { en: 'Uri', de: 'Uri', fr: 'Uri', it: 'Uri' },
    hasSeoPage: true
  },
  
  'Valais': {
    seoSlugs: { en: 'valais', de: 'wallis', fr: 'valais', it: 'vallese' },
    mapboxNames: { en: 'Valais', de: 'Kanton Wallis', fr: 'canton du Valais', it: 'Vallese' },
    strapiNames: { en: 'Valais', de: 'Wallis', fr: 'Valais', it: 'Vallese' },
    hasSeoPage: true
  },
  
  'Vaud': {
    seoSlugs: { en: 'vaud', de: 'waadt', fr: 'vaud', it: 'vaud' },
    mapboxNames: { en: 'Vaud', de: 'Kanton Waadt', fr: 'canton de Vaud', it: 'Vaud' },
    strapiNames: { en: 'Vaud', de: 'Waadt', fr: 'Vaud', it: 'Vaud' },
    hasSeoPage: true
  },
  'Zurich': {
    seoSlugs: { en: 'zurich', de: 'zurich', fr: 'zurich', it: 'zurigo' },
    mapboxNames: { en: 'Zürich', de: 'Kanton Zürich', fr: 'canton de Zurich', it: 'Zurigo' },
    strapiNames: { en: 'Zurich', de: 'Zürich', fr: 'Zurich', it: 'Zurigo' },
    hasSeoPage: true
  },
  'Aargau': {
    seoSlugs: { en: 'aargau', de: 'aargau', fr: 'argovie', it: 'argovia' },
    mapboxNames: { en: 'Aargau', de: 'Kanton Aargau', fr: "canton d'Argovie", it: 'Argovia' },
    strapiNames: { en: 'Aargau', de: 'Aargau', fr: 'Argovie', it: 'Argovia' },
    hasSeoPage: true
  },

  'Fribourg': {
    seoSlugs: { en: 'fribourg', de: 'freiburg', fr: 'fribourg', it: 'friburgo' },
    mapboxNames: { en: 'Fribourg', de: 'Kanton Freiburg', fr: 'canton de Fribourg', it: 'Friburgo' },
    strapiNames: { en: 'Fribourg', de: 'Freiburg', fr: 'Fribourg', it: 'Friburgo' },
    hasSeoPage: true
  },

  'Schaffhausen': {
    seoSlugs: { en: 'schaffhausen', de: 'schaffhausen', fr: 'schaffhouse', it: 'sciaffusa' },
    mapboxNames: { en: 'Schaffhausen', de: 'Kanton Schaffhausen', fr: 'canton de Schaffhouse', it: 'Sciaffusa' },
    strapiNames: { en: 'Schaffhausen', de: 'Schaffhausen', fr: 'Schaffhouse', it: 'Sciaffusa' },
    hasSeoPage: true
  },
  'Thurgau': {
    seoSlugs: { en: 'thurgau', de: 'thurgau', fr: 'thurgovie', it: 'turgovia' },
    mapboxNames: { en: 'Thurgau', de: 'Kanton Thurgau', fr: 'canton de Thurgovie', it: 'cantone Turgovia' },
    strapiNames: { en: 'Thurgau', de: 'Thurgau', fr: 'Thurgau', it: 'Thurgau' },
    hasSeoPage: true
  },

  'Zug': {
    seoSlugs: { en: 'zug', de: 'zug', fr: 'zoug', it: 'zug' },
    mapboxNames: { en: 'Zug', de: 'Kanton Zug', fr: 'canton de Zoug', it: 'Zugo' },
    strapiNames: { en: 'Zug', de: 'Zug', fr: 'Zoug', it: 'Zug' },
    hasSeoPage: true
  },

  'Solothurn': {
    seoSlugs: { en: 'solothurn', de: 'solothurn', fr: 'soleure', it: 'soletta' },
    mapboxNames: { en: 'Solothurn', de: 'Kanton Solothurn', fr: 'canton de Soleure', it: 'Soletta' },
    strapiNames: { en: 'Solothurn', de: 'Solothurn', fr: 'Soleure', it: 'Soletta' },
    hasSeoPage: true
  },

  'Basel-Stadt': {
    seoSlugs: { en: 'basel', de: 'basel', fr: 'bale', it: 'basilea' },
    mapboxNames: { en: 'Basel-Stadt', de: 'Kanton Basel-Stadt', fr: 'canton de Bâle-Ville', it: 'Basilea Città' },
    strapiNames: { en: 'Basel', de: 'Basel', fr: 'Bâle', it: 'Basilea' },
    hasSeoPage: true
  },

    'Basel-Landschaft': {
    seoSlugs: { en: 'basel', de: 'basel', fr: 'bale', it: 'basilea' },
    mapboxNames: { en: 'Basel-Landschaft', de: 'Kanton Basel-Landschaft', fr: 'canton de Bâle-Campagne', it: 'Basilea Campagna' },
    strapiNames: { en: 'Basel', de: 'Basel', fr: 'Bâle', it: 'Basilea' },
    hasSeoPage: true
  },

  'Neuchatel': {
    seoSlugs: { en: 'neuchatel', de: 'neuenburg', fr: 'neuchatel', it: 'neuchatel' },
    mapboxNames: { en: 'Neuchâtel', de: 'Kanton Neuenburg', fr: 'canton de Neuchâtel', it: 'Canton Neuchâtel' },
    strapiNames: { en: 'Neuchatel', de: 'Neuenburg', fr: 'Neuchâtel', it: 'Neuchâtel' },
    hasSeoPage: true
  },

  'Geneva': {
    seoSlugs: { en: 'geneva', de: 'genf', fr: 'geneve', it: 'ginevra' },
    mapboxNames: { en: 'Geneva', de: 'Kanton Genf', fr: 'canton de Genève', it: 'Ginevra' },
    strapiNames: { en: 'Geneva', de: 'Genf', fr: 'Genève', it: 'Ginevra' },
    hasSeoPage: true
  },
  
  // ============================================================================
  // ITALY (9 regions with SEO pages)
  // ============================================================================
  
  'Aosta Valley': {
    seoSlugs: { en: 'aosta-valley', de: 'aostatal', fr: 'vallee-d-aoste', it: 'valle-d-aosta' },
    mapboxNames: { en: 'Aosta Valley', de: 'Aostatal', fr: 'Vallée d\'Aoste', it: 'Valle d\'Aosta' },
    strapiNames: { en: 'Aosta Valley', de: 'Aostatal', fr: 'Vallée d\'Aoste', it: 'Valle d\'Aosta' },
    hasSeoPage: true
  },
  
  'Friuli Venezia Giulia': {
    seoSlugs: { en: 'friuli-venezia-giulia', de: 'friaul-julisch-venetien', fr: 'frioul-venetie-julienne', it: 'friuli-venezia-giulia' },
    mapboxNames: { en: 'Friuli Venezia Giulia', de: 'Friaul-Julisch Venetien', fr: 'Frioul-Vénétie Julienne', it: 'Friuli-Venezia Giulia' },
    strapiNames: { en: 'Friuli-Venezia Giulia', de: 'Friaul-Julisch Venetien', fr: 'Frioul-Vénétie Julienne', it: 'Friuli-Venezia Giulia' },
    hasSeoPage: true,
    note: 'Mapbox EN uses space, Strapi uses hyphen'
  },
  
  'Liguria': {
    seoSlugs: { en: 'liguria', de: 'ligurien', fr: 'ligurie', it: 'liguria' },
    mapboxNames: { en: 'Liguria', de: 'Ligurien', fr: 'Ligurie', it: 'Liguria' },
    strapiNames: { en: 'Liguria', de: 'Ligurien', fr: 'Ligurie', it: 'Liguria' },
    hasSeoPage: true
  },
  
  'Lombardy': {
    seoSlugs: { en: 'lombardy', de: 'lombardei', fr: 'lombardie', it: 'lombardia' },
    mapboxNames: { en: 'Lombardy', de: 'Lombardei', fr: 'Lombardie', it: 'Lombardia' },
    strapiNames: { en: 'Lombardy', de: 'Lombardei', fr: 'Lombardie', it: 'Lombardia' },
    hasSeoPage: true
  },
  
  'Piedmont': {
    seoSlugs: { en: 'piedmont', de: 'piemont', fr: 'piemont', it: 'piemonte' },
    mapboxNames: { en: 'Piedmont', de: 'Piemont', fr: 'Piémont', it: 'Piemonte' },
    strapiNames: { en: 'Piedmont', de: 'Piemont', fr: 'Piémont', it: 'Piemonte' },
    hasSeoPage: true
  },
  
  'Trentino-South Tyrol': {
    seoSlugs: { en: 'trentino-alto-adige', de: 'trentino-sudtirol', fr: 'trentin-haut-adige', it: 'trentino-alto-adige' },
    mapboxNames: { en: 'Trentino-South Tyrol', de: 'Trentino-Südtirol', fr: 'Trentin-Haut-Adige', it: 'Trentino-Alto Adige' },
    strapiNames: { en: 'Trentino-Alto Adige', de: 'Trentino-Südtirol', fr: 'Trentin-Haut-Adige', it: 'Trentino-Alto Adige' },
    hasSeoPage: true,
    note: 'Mapbox EN uses "South Tyrol", Strapi uses Italian "Alto Adige"'
  },
  
  'Tuscany': {
    seoSlugs: { en: 'tuscany', de: 'toskana', fr: 'toscane', it: 'toscana' },
    mapboxNames: { en: 'Tuscany', de: 'Toskana', fr: 'Toscane', it: 'Toscana' },
    strapiNames: { en: 'Tuscany', de: 'Toskana', fr: 'Toscane', it: 'Toscana' },
    hasSeoPage: true
  },
  
  'Umbria': {
    seoSlugs: { en: 'umbria', de: 'umbrien', fr: 'ombrie', it: 'umbria' },
    mapboxNames: { en: 'Umbria', de: 'Umbrien', fr: 'Ombrie', it: 'Umbria' },
    strapiNames: { en: 'Umbria', de: 'Umbrien', fr: 'Ombrie', it: 'Umbria' },
    hasSeoPage: true
  },
  
  'Veneto': {
    seoSlugs: { en: 'veneto', de: 'venetien', fr: 'venetie', it: 'veneto' },
    mapboxNames: { en: 'Veneto', de: 'Venetien', fr: 'Vénétie', it: 'Veneto' },
    strapiNames: { en: 'Veneto', de: 'Venetien', fr: 'Vénétie', it: 'Veneto' },
    hasSeoPage: true
  },
  
  // Italy - Regions in production data WITHOUT SEO pages
  // Italy - Regions (add Mapbox names and enable SEO pages)
  'Abruzzo': {
    seoSlugs: { en: 'abruzzo', de: 'abruzzen', fr: 'abruzzes', it: 'abruzzo' },
    mapboxNames: { en: 'Abruzzo', de: 'Abruzzen', fr: 'Abruzzes', it: 'Abruzzo' },
    strapiNames: { en: 'Abruzzo', de: 'Abruzzen', fr: 'Abruzzes', it: 'Abruzzo' },
    hasSeoPage: true
  },

  'Emilia-Romagna': {
    seoSlugs: { en: 'emilia-romagna', de: 'emilia-romagna', fr: 'emilie-romagne', it: 'emilia-romagna' },
    mapboxNames: { en: 'Emilia-Romagna', de: 'Emilia-Romagna', fr: 'Émilie-Romagne', it: 'Emilia-Romagna' },
    strapiNames: { en: 'Emilia-Romagna', de: 'Emilia-Romagna', fr: 'Émilie-Romagne', it: 'Emilia-Romagna' },
    hasSeoPage: true
  },

  'Lazio': {
    seoSlugs: { en: 'lazio', de: 'latium', fr: 'latium', it: 'lazio' },
    mapboxNames: { en: 'Lazio', de: 'Latium', fr: 'Latium', it: 'Lazio' },
    strapiNames: { en: 'Lazio', de: 'Latium', fr: 'Latium', it: 'Lazio' },
    hasSeoPage: true
  },

  'Marche': {
    seoSlugs: { en: 'marche', de: 'marken', fr: 'marches', it: 'marche' },
    mapboxNames: { en: 'Marche', de: 'Marken', fr: 'Marches', it: 'Marche' },
    strapiNames: { en: 'Marche', de: 'Marken', fr: 'Marches', it: 'Marche' },
    hasSeoPage: true
  },

  'Sardinia': {
    seoSlugs: { en: 'sardinia', de: 'sardinien', fr: 'sardaigne', it: 'sardegna' },
    mapboxNames: { en: 'Sardinia', de: 'Sardinien', fr: 'Sardaigne', it: 'Sardegna' },
    strapiNames: { en: 'Sardinia', de: 'Sardinien', fr: 'Sardaigne', it: 'Sardegna' },
    hasSeoPage: true
  },

  'Sicily': {
    seoSlugs: { en: 'sicily', de: 'sizilien', fr: 'sicile', it: 'sicilia' },
    mapboxNames: { en: 'Sicily', de: 'Sizilien', fr: 'Sicile', it: 'Sicilia' },
    strapiNames: { en: 'Sicily', de: 'Sizilien', fr: 'Sicile', it: 'Sicilia' },
    hasSeoPage: true
  },
  
  // Italy - Province/City level (should map to parent region)
  'Turin': {
    seoSlugs: { en: 'piedmont', de: 'piemont', fr: 'piemont', it: 'piemonte' },
    mapboxNames: { en: 'Turin', de: 'Metropolitanstadt Turin', fr: 'ville métropolitaine de Turin', it: 'città metropolitana di Torino' },
    strapiNames: { en: 'Piedmont', de: 'Piemont', fr: 'Piémont', it: 'Piemonte' },
    hasSeoPage: true,
    note: '⚠️ Province-level: Turin listings redirect to Piedmont SEO page'
  },
  
  'Livorno': {
    seoSlugs: { en: 'tuscany', de: 'toskana', fr: 'toscane', it: 'toscana' },
    mapboxNames: { en: 'Livorno', de: 'Provinz Livorno', fr: 'Livourne', it: 'provincia di Livorno' },
    strapiNames: { en: 'Tuscany', de: 'Toskana', fr: 'Toscane', it: 'Toscana' },
    hasSeoPage: true,
    note: '⚠️ Province-level: Livorno listings redirect to Tuscany SEO page'
  },
  
  // ============================================================================
  // AUSTRIA (5 regions with SEO pages)
  // ============================================================================
  
  'Carinthia': {
    seoSlugs: { en: 'carinthia', de: 'karnten', fr: 'carinthie', it: 'carinzia' },
    mapboxNames: { en: 'Carinthia', de: 'Kärnten', fr: 'Carinthie', it: 'Carinzia' },
    strapiNames: { en: 'Carinthia', de: 'Kärnten', fr: 'Carinthie', it: 'Carinzia' },
    hasSeoPage: true
  },
  
  'Upper Austria': {
    seoSlugs: { en: 'upper-austria', de: 'oberosterreich', fr: 'haute-autriche', it: 'alta-austria' },
    mapboxNames: { en: 'Upper Austria', de: 'Oberösterreich', fr: 'Haute-Autriche', it: 'Alta Austria' },
    strapiNames: { en: 'Upper Austria', de: 'Oberösterreich', fr: 'Haute-Autriche', it: 'Alta Austria' },
    hasSeoPage: true
  },
  
  'Salzburg': {
    seoSlugs: { en: 'salzburg', de: 'salzburg', fr: 'salzbourg', it: 'salisburgo' },
    mapboxNames: { en: 'Salzburg', de: 'Land Salzburg', fr: 'Salzbourg', it: 'Salisburghese' },
    strapiNames: { en: 'Salzburg', de: 'Salzburg', fr: 'Salzbourg', it: 'Salisburgo' },
    hasSeoPage: true,
    note: 'Mapbox DE uses "Land Salzburg", Strapi uses "Salzburg". Mapbox IT uses "Salisburghese", Strapi uses "Salisburgo"'
  },
  
  'Tyrol': {
    seoSlugs: { en: 'tyrol', de: 'tirol', fr: 'tyrol', it: 'tirolo' },
    mapboxNames: { en: 'Tyrol', de: 'Tirol', fr: 'Tyrol', it: 'Tirolo' },
    strapiNames: { en: 'Tyrol', de: 'Tirol', fr: 'Tyrol', it: 'Tirolo' },
    hasSeoPage: true
  },
  
  'Vorarlberg': {
    seoSlugs: { en: 'vorarlberg', de: 'vorarlberg', fr: 'vorarlberg', it: 'vorarlberg' },
    mapboxNames: { en: 'Vorarlberg', de: 'Vorarlberg', fr: 'Vorarlberg', it: 'Vorarlberg' },
    strapiNames: { en: 'Vorarlberg', de: 'Vorarlberg', fr: 'Vorarlberg', it: 'Vorarlberg' },
    hasSeoPage: true
  },
  
  'Styria': {
    seoSlugs: { en: 'styria', de: 'steiermark', fr: 'styrie', it: 'stiria' },
    mapboxNames: { en: 'Styria', de: 'Steiermark', fr: 'Styrie', it: 'Stiria' },
    strapiNames: { en: 'Styria', de: 'Steiermark', fr: 'Styrie', it: 'Stiria' },
    hasSeoPage: true
  },
  
  // ============================================================================
  // FRANCE (2 regions with SEO pages)
  // ============================================================================
  
  'Auvergne-Rhône-Alpes': {
    seoSlugs: { en: 'auvergne-rhone-alpes', de: 'auvergne-rhone-alpen', fr: 'auvergne-rhone-alpes', it: 'alvernia-rodano-alpi' },
    mapboxNames: { en: 'Auvergne-Rhône-Alpes', de: 'Auvergne-Rhône-Alpes', fr: 'Auvergne-Rhône-Alpes', it: 'Alvernia-Rodano-Alpi' },
    strapiNames: { en: 'Auvergne-Rhône-Alpes', de: 'Auvergne-Rhône-Alpen', fr: 'Auvergne-Rhône-Alpes', it: 'Alvernia-Rodano-Alpi' },
    hasSeoPage: true,
    note: 'Strapi DE adds "-Alpen" ending'
  },
  
  'Provence-Alpes-Côte d\'Azur': {
    seoSlugs: { en: 'provence-alpes-cote-d-azur', de: 'provence-alpes-cote-d-azur', fr: 'provence-alpes-cote-d-azur', it: 'provenza-alpi-costa-azzurra' },
    mapboxNames: { en: 'Provence-Alpes-Côte d\'Azur', de: 'Provence-Alpes-Côte d\'Azur', fr: 'Provence-Alpes-Côte d\'Azur', it: 'Provenza-Alpi-Costa Azzurra' },
    strapiNames: { en: 'Provence-Alpes-Côte d\'Azur', de: 'Provence-Alpes-Côte d\'Azur', fr: 'Provence-Alpes-Côte d\'Azur', it: 'Provenza-Alpi-Costa Azzurra' },
    hasSeoPage: true
  },
  
  // France - Regions in production data WITHOUT SEO pages
  'Haute-Savoie': { seoSlug: 'haute-savoie', hasSeoPage: false, note: 'This is a department, not a region' },
  
  // ============================================================================
  // GERMANY (2 regions with SEO pages)
  // ============================================================================
  
  'Baden-Württemberg': {
    seoSlugs: { en: 'baden-wurttemberg', de: 'baden-wurttemberg', fr: 'bade-wurtemberg', it: 'baden-wurtemberg' },
    mapboxNames: { en: 'Baden-Württemberg', de: 'Baden-Württemberg', fr: 'Bade-Wurtemberg', it: 'Baden-Württemberg' },
    strapiNames: { en: 'Baden-Württemberg', de: 'Baden-Württemberg', fr: 'Bade-Wurtemberg', it: 'Baden-Württemberg' },
    hasSeoPage: true
  },
  
  'Bavaria': {
    seoSlugs: { en: 'bavaria', de: 'bayern', fr: 'baviere', it: 'baviera' },
    mapboxNames: { en: 'Bavaria', de: 'Bayern', fr: 'Bavière', it: 'Baviera' },
    strapiNames: { en: 'Bavaria', de: 'Bayern', fr: 'Bavière', it: 'Baviera' },
    hasSeoPage: true
  },
  
  // ============================================================================
  // SLOVENIA (2 regions with SEO pages)
  // ============================================================================
  
  'Upper Carniola': {
    seoSlugs: { en: 'gorenjska', de: 'gorenjska', fr: 'gorenjska', it: 'gorenjska' },
    mapboxNames: { en: 'Upper Carniola', de: 'Oberkrain', fr: 'Haute-Carniole', it: 'Alta Carniola' },
    strapiNames: { en: 'Gorenjska', de: 'Gorenjska', fr: 'Gorenjska', it: 'Gorenjska' },
    hasSeoPage: true,
    note: '⚠️ NAME MISMATCH: Mapbox uses English "Upper Carniola", Strapi uses Slovenian "Gorenjska"'
  },
  
  'Gorizia': {
    seoSlugs: { en: 'goriska', de: 'goriska', fr: 'goriska', it: 'goriska' },
    mapboxNames: { en: 'Gorizia', de: 'Görz', fr: 'Gorizia', it: 'Gorizia' },
    strapiNames: { en: 'Goriška', de: 'Goriška', fr: 'Goriška', it: 'Goriška' },
    hasSeoPage: true,
    note: '⚠️ NAME MISMATCH: Mapbox uses Italian "Gorizia", Strapi uses Slovenian "Goriška"'
  },
  
  // Slovenia - Municipality level (needs parent region)
  'Bled': {
    seoSlugs: { en: 'gorenjska', de: 'gorenjska', fr: 'gorenjska', it: 'gorenjska' },
    mapboxNames: { en: 'Bled', de: 'Gemeinde Bled', fr: 'Bled', it: 'Comune di Bled' },
    strapiNames: { en: 'Gorenjska', de: 'Gorenjska', fr: 'Gorenjska', it: 'Gorenjska' },
    hasSeoPage: true,
    note: '⚠️ Municipality-level: Bled is in Upper Carniola (Gorenjska) region'
  },
  
  // ============================================================================
  // NORWAY (2 regions with SEO pages)
  // ============================================================================
  
  'Nordland': {
    seoSlugs: { en: 'nordland', de: 'nordland', fr: 'nordland', it: 'nordland' },
    mapboxNames: { en: 'Nordland', de: 'Nordland', fr: 'Nordland', it: 'Nordland' },
    strapiNames: { en: 'Nordland', de: 'Nordland', fr: 'Nordland', it: 'Nordland' },
    hasSeoPage: true
  },
  
  'Troms og Finnmark': {
    seoSlugs: { en: 'troms-og-finnmark', de: 'troms-og-finnmark', fr: 'troms-og-finnmark', it: 'troms-og-finnmark' },
    mapboxNames: { en: 'Troms og Finnmark', de: 'Troms og Finnmark', fr: 'Troms-et-Finnmark', it: 'Troms e Finnmark' },
    strapiNames: { en: 'Troms og Finnmark', de: 'Troms og Finnmark', fr: 'Troms og Finnmark', it: 'Troms og Finnmark' },
    hasSeoPage: true,
    note: 'Merged county in 2020. Mapbox may return "Finnmark" or "Troms" separately'
  },
  
  // Norway - Old merged regions (redirect to Troms og Finnmark)
  'Finnmark': {
    seoSlugs: { en: 'troms-og-finnmark', de: 'troms-og-finnmark', fr: 'troms-og-finnmark', it: 'troms-og-finnmark' },
    hasSeoPage: true,
    note: 'Old name, now merged into Troms og Finnmark. Redirect to merged region'
  },
  'Troms': {
    seoSlugs: { en: 'troms-og-finnmark', de: 'troms-og-finnmark', fr: 'troms-og-finnmark', it: 'troms-og-finnmark' },
    hasSeoPage: true,
    note: 'Old name, now merged into Troms og Finnmark. Redirect to merged region'
  },
  'Vestland': { seoSlug: 'vestland', hasSeoPage: false },
  'Møre og Romsdal': { seoSlug: 'more-og-romsdal', hasSeoPage: false },
  
  // ============================================================================
  // FINLAND (1 region with SEO page)
  // ============================================================================
  
  'Lapland': {
    seoSlugs: { en: 'lapland', de: 'lappland', fr: 'laponie', it: 'lapponia' },
    mapboxNames: { en: 'Lapland', de: 'Lappland', fr: 'Laponie', it: 'Regione della Lapponia' },
    strapiNames: { en: 'Lapland', de: 'Lappland', fr: 'Laponie', it: 'Lapponia' },
    hasSeoPage: true,
    note: 'Mapbox IT uses "Regione della Lapponia", Strapi uses "Lapponia"'
  },
  
  // ============================================================================
  // SPAIN (1 region with SEO page)
  // ============================================================================
  
  'Extremadura': {
    seoSlugs: { en: 'extremadura', de: 'extremadura', fr: 'estremadure', it: 'estremadura' },
    mapboxNames: { en: 'Extremadura', de: 'Extremadura', fr: 'Estrémadure', it: 'Estremadura' },
    strapiNames: { en: 'Extremadura', de: 'Extremadura', fr: 'Estrémadure', it: 'Estremadura' },
    hasSeoPage: true
  },
  
  // Spain - Regions (add Mapbox names and enable SEO pages)
  'Andalusia': {
    seoSlugs: { en: 'andalusia', de: 'andalusien', fr: 'andalousie', it: 'andalusia' },
    mapboxNames: { en: 'Andalusia', de: 'Andalusien', fr: 'Andalousie', it: 'Andalusia' },
    strapiNames: { en: 'Andalusia', de: 'Andalusien', fr: 'Andalousie', it: 'Andalusia' },
    hasSeoPage: true
  },

  'Aragon': {
    seoSlugs: { en: 'aragon', de: 'aragon', fr: 'aragon', it: 'aragona' },
    mapboxNames: { en: 'Aragon', de: 'Aragonien', fr: 'Aragon', it: 'Aragona' },
    strapiNames: { en: 'Aragon', de: 'Aragon', fr: 'Aragon', it: 'Aragona' },
    hasSeoPage: true
  },

  'Asturias': {
    seoSlugs: { en: 'asturias', de: 'asturien', fr: 'asturies', it: 'asturie' },
    mapboxNames: { en: 'Asturias', de: 'Asturien', fr: 'Asturies', it: 'Asturie' },
    strapiNames: { en: 'Asturias', de: 'Asturias', fr: 'Asturies', it: 'Asturie' },
    hasSeoPage: true
  },

  'Basque Autonomous Community': {
    seoSlugs: { en: 'basque-country', de: 'baskenland', fr: 'pays-basque', it: 'paesi-baschi' },
    mapboxNames: { en: 'Basque Autonomous Community', de: 'Autonome Gemeinschaft Baskenland', fr: 'Pays basque', it: 'Paesi Baschi' },
    strapiNames: { en: 'Basque Country', de: 'Baskenland', fr: 'Pays basque', it: 'Paesi Baschi' },
    hasSeoPage: true
  },

  'Canary Islands': {
    seoSlugs: { en: 'canary-islands', de: 'kanarische-inseln', fr: 'iles-canaries', it: 'isole-canarie' },
    mapboxNames: { en: 'Canary Islands', de: 'Kanarische Inseln', fr: 'îles Canaries', it: 'Isole Canarie' },
    strapiNames: { en: 'Canary Islands', de: 'Kanarische Inseln', fr: 'Îles Canaries', it: 'Isole Canarie' },
    hasSeoPage: true
  },

  'Castile and León': {
    seoSlugs: { en: 'castile-and-leon', de: 'kastilien-und-leon', fr: 'castille-et-leon', it: 'castiglia-e-leon' },
    mapboxNames: { en: 'Castile and León', de: 'Kastilien und León', fr: 'Castille-et-León', it: 'Castiglia e León' },
    strapiNames: { en: 'Castile and León', de: 'Kastilien und León', fr: 'Castille-et-León', it: 'Castiglia e León' },
    hasSeoPage: true
  },

  'Catalonia': {
    seoSlugs: { en: 'catalonia', de: 'katalonien', fr: 'catalogne', it: 'catalogna' },
    mapboxNames: { en: 'Catalonia', de: 'Katalonien', fr: 'Catalogne', it: 'Catalogna' },
    strapiNames: { en: 'Catalonia', de: 'Katalonien', fr: 'Catalogne', it: 'Catalogna' },
    hasSeoPage: true
  },

  'Madrid': {
    seoSlugs: { en: 'madrid', de: 'madrid', fr: 'madrid', it: 'madrid' },
    mapboxNames: { en: 'Madrid', de: 'Autonome Gemeinschaft Madrid', fr: 'Madrid', it: 'Madrid' },
    strapiNames: { en: 'Madrid', de: 'Madrid', fr: 'Madrid', it: 'Madrid' },
    hasSeoPage: true
  },

  'Navarre': {
    seoSlugs: { en: 'navarre', de: 'navarra', fr: 'navarre', it: 'navarra' },
    mapboxNames: { en: 'Navarre', de: 'Navarra', fr: 'Navarre', it: 'Navarra' },
    strapiNames: { en: 'Navarre', de: 'Navarra', fr: 'Navarre', it: 'Navarra' },
    hasSeoPage: true
  },

  'Valencian Community': {
    seoSlugs: { en: 'valencia', de: 'valencian-community', fr: 'valence', it: 'valencia' },
    mapboxNames: { en: 'Valencian Community', de: 'Valencianische Gemeinschaft', fr: 'Communauté valencienne', it: 'Comunità Valenciana' },
    strapiNames: { en: 'Valencia', de: 'Valencianische Gemeinschaft', fr: 'Valence', it: 'Valencia' },
    hasSeoPage: true
  },
  
  // ============================================================================
  // OTHER COUNTRIES - Regions without SEO pages
  // ============================================================================
  
  // Argentina
  'Mendoza': { seoSlug: 'mendoza', hasSeoPage: false },
  
  // Canada
  'British Columbia': { seoSlug: 'british-columbia', hasSeoPage: false },
  
  // Japan
  '北海道': { seoSlug: 'hokkaido', hasSeoPage: false, note: 'Hokkaido - Japanese name used by Mapbox' },
  
  // United States
  'California': { seoSlug: 'california', hasSeoPage: false },
  'Colorado': { seoSlug: 'colorado', hasSeoPage: false },
  
  // Svalbard and Jan Mayen (Norwegian territory)
  'Svalbard and Jan Mayen': { seoSlug: 'svalbard-and-jan-mayen', hasSeoPage: false },
};

// Build reverse lookup index: All language variants → region data
// This allows lookup with any language's Mapbox name
const MAPBOX_NAME_TO_REGION = {};

Object.entries(REGION_DATA).forEach(([englishName, data]) => {
  // Add English name as key
  MAPBOX_NAME_TO_REGION[englishName] = data;
  
  // Add all language variants as keys
  if (data.mapboxNames) {
    Object.values(data.mapboxNames).forEach(name => {
      if (name && name !== englishName) {
        MAPBOX_NAME_TO_REGION[name] = data;
      }
    });
  }
});

// Export for use in other files
export const REGION_SEO_MAPPING = REGION_DATA;

/**
 * Get localized SEO slug for a region based on language
 * 
 * @param {string} mapboxRegion - Region name from Mapbox in ANY language
 *                                 e.g., "Ticino", "Kanton Tessin", "Canton Ticino"
 * @param {string} lang - Language code or name ('en', 'de', 'fr', 'it', 'english', 'french', 'german', 'italian')
 * @returns {string|null} - Localized SEO slug or null if not found
 * 
 * @example
 * getRegionSeoSlug("Kanton Tessin", "german") // → "tessin" (German slug)
 * getRegionSeoSlug("Kanton Tessin", "de")     // → "tessin"
 * getRegionSeoSlug("Canton Ticino", "italian") // → "ticino" (Italian slug)
 * getRegionSeoSlug("Canton Ticino", "it")      // → "ticino"
 */
export const getRegionSeoSlug = (mapboxRegion, lang) => {
  if (!mapboxRegion) return null;
  const data = MAPBOX_NAME_TO_REGION[mapboxRegion];
  
  // Convert full language name to short code if needed
  const langCode = LANG_NAME_TO_CODE[lang] || lang;
  
  // Return localized slug, fallback to English slug, then null
  return data?.seoSlugs?.[langCode] || data?.seoSlugs?.en || data?.seoSlug || null;
};

/**
 * Check if a region has an SEO page in Strapi
 * 
 * @param {string} mapboxRegion - Region name from Mapbox in ANY language
 * @returns {boolean} - True if SEO page exists
 * 
 * @example
 * hasRegionSeoPage("Canton Ticino") // → true
 * hasRegionSeoPage("Abruzzo")       // → false (no SEO page)
 */
export const hasRegionSeoPage = (mapboxRegion) => {
  if (!mapboxRegion) return false;
  const data = MAPBOX_NAME_TO_REGION[mapboxRegion];
  return data?.hasSeoPage || false;
};

/**
 * Get complete region data for debugging/inspection
 * 
 * @param {string} mapboxRegion - Region name from Mapbox in ANY language
 * @returns {object|null} - Complete region data or null
 */
export const getRegionData = (mapboxRegion) => {
  if (!mapboxRegion) return null;
  return MAPBOX_NAME_TO_REGION[mapboxRegion] || null;
};

/**
 * Get Strapi region name for breadcrumb display in the requested language
 * Falls back to Mapbox name if no Strapi name defined
 * 
 * BUSINESS CASE: Match locale - Mapbox language → Strapi language
 * 
 * @param {string} mapboxRegion - Region name from Mapbox in ANY language
 * @param {string} lang - Language code or name ('en', 'de', 'fr', 'it', 'english', 'french', 'german', 'italian')
 * @returns {string|null} - Localized Strapi name for display in the SAME language
 * 
 * @example
 * // Accepts both short codes and full names:
 * getRegionStrapiName("Lombardia", "italian") // → "Lombardia"
 * getRegionStrapiName("Lombardia", "it")      // → "Lombardia"
 * 
 * // French: Mapbox French → Strapi French
 * getRegionStrapiName("Lombardie", "french") // → "Lombardie"
 * getRegionStrapiName("Lombardie", "fr")     // → "Lombardie"
 * 
 * // German: Mapbox German → Strapi German
 * getRegionStrapiName("Lombardei", "german") // → "Lombardei"
 * getRegionStrapiName("Lombardei", "de")     // → "Lombardei"
 */
export const getRegionStrapiName = (mapboxRegion, lang) => {
  if (!mapboxRegion) return null;
  const data = MAPBOX_NAME_TO_REGION[mapboxRegion];
  
  // Convert full language name to short code if needed
  const langCode = LANG_NAME_TO_CODE[lang] || lang;
  
  return data?.strapiNames?.[langCode] || mapboxRegion;
};
